// Result screen = share screen (design §4.4). Built immediately from local data
// (score, outcome vs the ghost we just raced) and refined when the server's
// verification lands (rank, chain, share code).

import { FEEDBACK_URL } from '../config';
import { t } from '../i18n';
import type { ChainSummary } from '../types';
import { el, enter } from './dom';
import { nicknameEditor } from './nickname';

export interface ResultButton {
  label: string;
  /** Buttons that need the verified run (share code, run id, server nickname)
   *  stay disabled until POST /api/runs comes back. */
  needsShareCode?: boolean;
  onClick: () => void;
}

/** One row of the rival ladder (§4bis.21), already localized by the caller. */
export interface LadderRow {
  /** `opponent` | `self` | `daily` — also the `ladder_click` prop. */
  id: string;
  /** 刚才的对手 / 本周的你 / 今日路人. */
  title: string;
  /** Who and how far: 「{name} · {n} 米」, or the empty-state sentence. */
  sub: string;
  /** Trailing call to action: 「差 {d} 米」 / 「再赢一次」. Null on empty rungs. */
  cta: string | null;
  /** Empty rungs are shown, greyed and inert (design: say what fills them). */
  disabled?: boolean;
  onClick?: () => void;
}

export interface ResultOptions {
  title: string;
  distanceM: number;
  metersLabel: string;
  lines: readonly string[];
  primary: ResultButton;
  secondary?: ResultButton | null;
  /** Low-emphasis extra actions (design §4.4 keeps primary/secondary fixed). */
  extras?: readonly ResultButton[];
  /**
   * Rival ladder (§4bis.21). Null → the section is never created, which is what
   * every device outside the `LADDER_PCT` arm gets: the screen below is the
   * shipped one, node for node. Non-null → rendered under the actions and above
   * the chain board, and refreshed by `setLadder` when the two rungs that need
   * the server arrive.
   */
  ladder?: { title: string; rows: readonly LadderRow[] } | null;
  /** Default-opponent runs show no chain, just the "start one" empty state. */
  showChain: boolean;
  chain: ChainSummary | null;
  chainEmptyText: string;
  /** Footer link that re-arms the stop-frame tutorial for the next run
   *  (design §2.3). Omitted → no link. */
  onTutorialAgain?: () => void;
}

/**
 * Should a button be born DISABLED, waiting for a share code?
 *
 * The invariant is about STATE, not about call order: a button needs a share
 * code only while there isn't one yet. Once `setShareReady` has run, anything
 * built afterwards is already live and must not be gated — there is no second
 * drain of `gated`, and `applySubmitted` runs once.
 *
 * This existed as an inline `if (cfg.needsShareCode)` and produced a button
 * that rendered and could never be tapped: `applySubmitted` called
 * `setShareReady()` (which enables every gated node AND empties the list) and
 * then, thirty lines later in the same function, `promotePrimary()` built a
 * fresh gated node that nothing would ever enable. It hit the FULL-RUN path —
 * the majority case — because `endRun` mounts the result screen before
 * submitting, so a full run always depends on promotion. The duel path escaped
 * by accident: its seal submits at death, so the answer lands while the seal is
 * still up and the button is built through the ordinary path.
 *
 * Fixing the ORDER would have left the same trap armed for the next caller who
 * adds anything after `setShareReady`. This fixes the rule.
 */
export function gateOnShareCode(needsShareCode: boolean, shareReady: boolean): boolean {
  return needsShareCode && !shareReady;
}

export class ResultView {
  private readonly screen: HTMLElement;
  private readonly noteEl: HTMLElement;
  private readonly chainEl: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly nickSlot: HTMLElement;
  private readonly gated: HTMLButtonElement[] = [];
  /** True once a share code exists. Buttons built after this are born live. */
  private shareReady = false;
  private chainEmptyText: string;
  /** The sub-lines under the distance, kept flat in the screen (no wrapper —
   *  the layout is a flex column with one gap) so `setLines` can swap them. */
  private lineEls: HTMLElement[];
  /** Null unless the device is in the ladder arm — no flag, no node. */
  private readonly ladderEl: HTMLElement | null;
  private readonly ladderRowsEl: HTMLElement | null;

  constructor(root: HTMLElement, opts: ResultOptions) {
    this.chainEmptyText = opts.chainEmptyText;
    this.nickSlot = el('div');
    this.titleEl = el('div', { class: 'result-title', text: opts.title });
    this.noteEl = el('div', { class: 'result-sub', text: t('result.submitting') });
    this.chainEl = el('div', { class: 'chain' });

    const distance = el('div', { class: 'result-distance' }, [
      el('b', { text: String(opts.distanceM) }),
      el('span', { text: opts.metersLabel }),
    ]);

    this.lineEls = opts.lines.map((line) => el('div', { class: 'result-sub', text: line }));

    const actions = el('div', { class: 'result-actions' }, [
      this.button(opts.primary, 'btn btn--primary'),
      opts.secondary ? this.button(opts.secondary, 'btn') : null,
      ...(opts.extras ?? []).map((b) => this.button(b, 'btn btn--ghost')),
    ]);

    if (opts.ladder) {
      this.ladderRowsEl = el('div', { class: 'ladder-rows' });
      this.ladderEl = el('div', { class: 'ladder' }, [
        el('div', { class: 'ladder-head', text: opts.ladder.title }),
        this.ladderRowsEl,
      ]);
      this.setLadder(opts.ladder.rows);
    } else {
      this.ladderEl = null;
      this.ladderRowsEl = null;
    }

    this.screen = el('div', { class: 'screen screen--result' }, [
      this.titleEl,
      distance,
      ...this.lineEls,
      this.noteEl,
      actions,
      this.ladderEl,
      el('div', { class: 'divider' }),
      opts.showChain ? this.chainEl : el('div', { class: 'chain-empty', text: opts.chainEmptyText }),
      el('div', { class: 'divider' }),
      this.nickSlot,
      el('div', { class: 'spacer' }),
      el('div', { class: 'feedback' }, [
        el('a', {
          text: t('feedback.link'),
          attrs: { href: FEEDBACK_URL, target: '_blank', rel: 'noopener noreferrer' },
        }),
        el('a', {
          text: t('privacy.link'),
          attrs: { href: '/privacy', target: '_blank', rel: 'noopener' },
        }),
        opts.onTutorialAgain ? tutorialAgainLink(opts.onTutorialAgain) : null,
      ]),
    ]);

    if (opts.showChain) this.setChain(opts.chain);
    root.append(this.screen);
    enter(this.screen);
  }

  private button(cfg: ResultButton, className: string): HTMLButtonElement {
    const node = el('button', {
      class: className,
      text: cfg.label,
      attrs: { type: 'button' },
    });
    node.addEventListener('click', cfg.onClick);
    if (gateOnShareCode(cfg.needsShareCode === true, this.shareReady)) {
      node.disabled = true;
      this.gated.push(node);
    }
    return node;
  }

  setTitle(title: string): void {
    this.titleEl.textContent = title;
  }

  /**
   * Replaces the sub-lines in place (H02 B4). Called when the server's own
   * record for this track turns out not to back the remembered best the screen
   * mounted with — the screen must not keep a number the rung below it denies.
   * Same nodes, same classes, same position: the layout is untouched.
   */
  setLines(lines: readonly string[]): void {
    const next = lines.map((line) => el('div', { class: 'result-sub', text: line }));
    for (const node of next) this.screen.insertBefore(node, this.noteEl);
    for (const node of this.lineEls) node.remove();
    this.lineEls = next;
  }

  setNote(text: string | null): void {
    this.noteEl.textContent = text ?? '';
    this.noteEl.style.display = text ? '' : 'none';
  }

  /**
   * §4bis.31: promote a verb into the primary slot once the server confirms the
   * run can actually offer it.
   *
   * The screen mounts BEFORE the submit lands, and whether a run can be handed
   * back is a server derivation (`handoffMintable`) — so the truthful mount is
   * 打败我 alone, and 帮我 appears only if the answer says the link will really
   * be a hand-back. Mounting with it and withdrawing it on a negative answer
   * would show an offer the product cannot keep, which is the exact failure
   * this whole change exists to remove.
   *
   * The old primary is demoted to a plain button rather than dropped: both
   * verbs belong on a qualifying death, and T2's denominator needs the
   * challenge share to still be reachable.
   *
   * The promoted button is LIVE the moment it appears. It is only reachable
   * from `applySubmitted`, i.e. after a share code exists, and `button()` now
   * decides gating from that state rather than from where it happens to be
   * called — see `gateOnShareCode`. An offer the player can see and cannot
   * press is the same broken promise as a link that does not do what it says.
   */
  promotePrimary(cfg: ResultButton): void {
    const actions = this.screen.querySelector('.result-actions');
    if (!actions) return;
    // Design §4.4 fixes ONE primary and ONE secondary; extras are low-emphasis.
    // Promoting a verb has to preserve that, not append to it: demoting only
    // the old primary left two filled rows plus the new one, and a screen with
    // three equal shouts has no primary action at all — which defeats the point
    // of promoting one. So the whole ladder shifts down a rung.
    const previousPrimary = actions.firstElementChild;
    const previousSecondary = previousPrimary?.nextElementSibling ?? null;
    if (previousPrimary instanceof HTMLButtonElement) previousPrimary.className = 'btn';
    // Already-ghost extras are unaffected by this (same class), so it is safe
    // when the screen had no secondary of its own.
    if (previousSecondary instanceof HTMLButtonElement) {
      previousSecondary.className = 'btn btn--ghost';
    }
    const node = this.button(cfg, 'btn btn--primary');
    actions.insertBefore(node, actions.firstChild);
  }

  /** Enables the share/challenge buttons once a share code exists. */
  setShareReady(): void {
    this.shareReady = true;
    for (const b of this.gated) b.disabled = false;
    this.gated.length = 0;
  }

  /** Mounts the nickname editor once the server tells us the current name
   *  (prefilled with the generated fallback name, design §4.6). */
  setNickname(nickname: string): void {
    while (this.nickSlot.firstChild) this.nickSlot.removeChild(this.nickSlot.firstChild);
    this.nickSlot.append(nicknameEditor({ initial: nickname }));
  }

  /**
   * Replaces the ladder rows (§4bis.21). Called once at construction with the
   * rung that needs no server — the opponent just raced — and again when
   * `/api/self-best` and `/api/daily-rival` land. A no-op when the ladder is
   * off, so callers never have to ask.
   */
  setLadder(rows: readonly LadderRow[]): void {
    const box = this.ladderRowsEl;
    if (!box) return;
    while (box.firstChild) box.removeChild(box.firstChild);
    for (const row of rows) box.append(this.ladderRow(row));
  }

  private ladderRow(row: LadderRow): HTMLElement {
    const node = el(
      'button',
      {
        class: row.disabled ? 'rung rung--empty' : 'rung',
        attrs: { type: 'button', 'data-rung': row.id },
      },
      [
        el('span', { class: 'rung-main' }, [
          el('span', { class: 'rung-title', text: row.title }),
          el('span', { class: 'rung-sub', text: row.sub }),
        ]),
        row.cta ? el('span', { class: 'rung-cta', text: row.cta }) : null,
      ],
    );
    if (row.disabled || !row.onClick) node.disabled = true;
    else node.addEventListener('click', row.onClick);
    return node;
  }

  setChain(chain: ChainSummary | null): void {
    const box = this.chainEl;
    while (box.firstChild) box.removeChild(box.firstChild);
    if (!chain || chain.size <= 0) {
      box.append(el('div', { class: 'chain-empty', text: this.chainEmptyText }));
      return;
    }

    const you = chain.you;
    box.append(
      el('div', { class: 'chain-head' }, [
        el('span', { text: t('chain.title') }),
        el('span', {
          text: t('chain.summary', { size: chain.size, rank: you ?? chain.size }),
        }),
      ]),
    );

    const top = chain.top.slice(0, 3);
    let youShown = false;
    top.forEach((entry, i) => {
      const rank = entry.rank ?? i + 1;
      const isYou = entry.isYou === true || entry.you === true || (you !== undefined && rank === you);
      if (isYou) youShown = true;
      box.append(this.chainRow(rank, entry.nickname, entry.scoreM, isYou));
    });

    if (!youShown && you !== undefined) {
      const mine = chain.top.find((e) => e.isYou === true || e.you === true || e.rank === you);
      box.append(this.chainRow(you, mine?.nickname ?? t('chain.you'), mine?.scoreM ?? 0, true));
    }

    if (chain.size <= 2) {
      box.append(el('div', { class: 'chain-empty', text: t('chain.pair') }));
    }
  }

  private chainRow(rank: number, name: string, scoreM: number, isYou: boolean): HTMLElement {
    return el('div', { class: isYou ? 'chain-row chain-row--you' : 'chain-row' }, [
      el('span', { class: 'chain-rank', text: String(rank) }),
      el('span', { class: 'chain-name', text: isYou ? `${name} · ${t('chain.you')}` : name }),
      el('span', { class: 'chain-score', text: String(scoreM) }),
    ]);
  }

  remove(): void {
    this.screen.remove();
  }
}

/** 「下一局重新教学」 — one click, then it becomes its own confirmation. There
 *  is no way to un-arm it on purpose: the next run consumes the flag anyway. */
function tutorialAgainLink(onArm: () => void): HTMLButtonElement {
  const node = el('button', {
    class: 'feedback-tutorial',
    text: t('tutorial.again'),
    attrs: { type: 'button' },
  });
  node.addEventListener('click', () => {
    onArm();
    node.textContent = t('tutorial.armed');
    node.disabled = true;
  });
  return node;
}
